import customSoftwareIcon from "assets/images/services/customSoftware.svg";
import enterpriseIntegrationIcon from "assets/images/services/enterpriseIntegration.svg";
import erpIntegrationIcon from "assets/images/services/erpIntegration.svg";
import apiDevelopmentIcon from "assets/images/services/apiDevelopment.svg";
import aiAutomationIcon from "assets/images/services/aiAutomation.svg";
import workflowAutomationIcon from "assets/images/services/workflowAutomation.svg";
import productionSupportIcon from "assets/images/services/productionSupport.svg";
import cloudDevopsIcon from "assets/images/services/cloudDevops.svg";

import customSoftwareImg from "assets/images/services/custom-software-development.webp";
import enterpriseIntegrationImg from "assets/images/services/enterprise-system-integration.webp";
import erpIntegrationImg from "assets/images/services/erp-integration.webp";
import apiDevelopmentImg from "assets/images/services/api-development-management.webp";
import aiAutomationImg from "assets/images/services/ai-automation.webp";
import workflowAutomationImg from "assets/images/services/workflow-automation.webp";
import productionSupportImg from "assets/images/services/production-support-maintenance.webp";
import cloudDevopsImg from "assets/images/services/cloud-devops-solutions.webp";

// Per-service content for the /services/* detail pages, from the services note
// with review adjustments: ERP is vendor-neutral (no Oracle Fusion/SAP/Dynamics
// names), API avoids naming specific protocols, and AI is framed as an emerging
// capability rather than an established service. The three services with Agri
// CTRM heritage carry an `expertise` block so the merged page presents a
// generic offering backed by proven CTRM/ETRM depth.
const SITE = "https://www.paplajsoftware.com";

const CTRM_EXPERTISE_TITLE = "Our CTRM/ETRM Expertise";

export const services = {
  "custom-software-development": {
    slug: "custom-software-development",
    name: "Custom Software Development",
    icon: customSoftwareIcon,
    image: customSoftwareImg,
    imageAlt: "Custom enterprise software across desktop, web and mobile",
    metaTitle: "Custom Software Development | Paplaj Software Services",
    metaDescription:
      "Custom enterprise software built around your business — web applications, SaaS, legacy modernization and cloud-native solutions, secure and scalable.",
    canonical: `${SITE}/services/custom-software-development`,
    headline:
      "Build Software That Fits Your Business, Not the Other Way Around",
    paragraphs: [
      "Every business has unique processes and challenges. Off-the-shelf software often forces organizations to adapt their workflows. At Paplaj Software Services, we design and develop custom software tailored to your business objectives, helping you improve efficiency, automate operations, and scale with confidence.",
      "Whether you're building a new application, modernizing a legacy system, or developing enterprise-grade platforms, we deliver secure, scalable, and future-ready solutions."
    ],
    features: [
      "Enterprise Web Applications",
      "Business Process Automation",
      "SaaS Product Development",
      "Legacy System Modernization",
      "Cloud-Native Applications",
      "Mobile & Responsive Applications",
      "Secure & Scalable Architecture",
      "Ongoing Enhancement & Support"
    ],
    expertise: {
      title: CTRM_EXPERTISE_TITLE,
      content:
        "We have deep, hands-on experience building Commodity Trading and Risk Management systems. Our team designs and develops scalable, secure CTRM/ETRM platforms tailored to complex trading operations — proof that we can deliver software for demanding, business-critical domains."
    },
    faqs: [
      {
        title: "How do you approach a custom software project?",
        content:
          "We start by understanding your business goals, processes and constraints, then design and build software tailored to your objectives — automating processes, providing real-time data, and helping you make informed decisions and optimize operations."
      },
      {
        title: "Is the software you build scalable for future growth?",
        content:
          "Yes. We design and build with scalability at its core, so the system can expand as your business grows — whether that's higher volumes, new products, or more users — adapting seamlessly to your evolving needs."
      },
      {
        title: "What security measures protect sensitive data?",
        content:
          "During design and development we incorporate advanced security measures to protect your sensitive data, including encryption for data at rest and in transit, and role-based access controls so only authorized personnel can access or modify critical information."
      },
      {
        title:
          "Can the software integrate with our internal and third-party systems?",
        content:
          "Yes. Our team works closely with functional experts to ensure integrations with internal software and third-party systems are seamless — through APIs or custom connectors — including ERP systems, accounting software and logistics platforms."
      },
      {
        title: "What support is offered after implementation?",
        content:
          "After implementation we provide support based on an agreed Service Level Agreement (SLA), including updates, security patches and performance enhancements to keep your system optimized as your needs evolve."
      }
    ],
    cta: "Let's Build Your Solution"
  },

  "enterprise-system-integration": {
    slug: "enterprise-system-integration",
    name: "Enterprise System Integration",
    icon: enterpriseIntegrationIcon,
    image: enterpriseIntegrationImg,
    imageAlt: "Enterprise systems connected through APIs, cloud and middleware",
    metaTitle: "Enterprise System Integration | Paplaj Software Services",
    metaDescription:
      "Securely connect enterprise systems, cloud platforms and third-party applications for reliable, real-time data exchange and streamlined operations.",
    canonical: `${SITE}/services/enterprise-system-integration`,
    headline: "Connect Systems. Streamline Operations. Accelerate Business.",
    paragraphs: [
      "Modern enterprises rely on multiple applications, cloud platforms, and business systems to operate efficiently. We help organizations connect these systems into a secure, scalable, and reliable integration ecosystem that enables seamless data exchange, process automation, and real-time business visibility.",
      "Our enterprise integration solutions are designed to eliminate data silos, simplify complex workflows, and support digital transformation using the most appropriate technologies, including APIs, middleware, message-driven integration, event-driven architecture, and hybrid cloud solutions. Looking for platform-specific integration or API implementation? Explore our ERP Integration and API Development & Management services."
    ],
    features: [
      "Enterprise Application Integration",
      "API & Web Service Integration",
      "Cloud Integration",
      "Hybrid Integration",
      "Message Queue Integration",
      "Data Transformation & Mapping",
      "Real-Time Synchronization",
      "Monitoring & Error Handling"
    ],
    expertise: {
      title: CTRM_EXPERTISE_TITLE,
      content:
        "We specialize in integrating Commodity Trading and Risk Management systems with internal platforms and third-party services, creating unified, real-time data flow across trading operations — experience we bring to enterprise integration in any sector."
    },
    faqs: [
      {
        title: "What types of platforms can you integrate?",
        content:
          "Our technical team works closely with functional experts to ensure integrations are seamless. Through APIs or custom connectors, we integrate enterprise systems with ERP systems, accounting software, logistics platforms and other third-party services."
      },
      {
        title: "How does integration improve operational efficiency?",
        content:
          "Integration streamlines workflows and automates data flow between systems, reducing manual input and minimizing errors for faster, more informed decision-making — so businesses operate more smoothly and respond quickly to market changes."
      },
      {
        title: "Can you integrate with our internal systems?",
        content:
          "Yes. Our team is proficient in integration techniques and collaborates closely with functional experts to ensure all integrations are tailored to your target systems, whether through APIs or custom connectors."
      },
      {
        title: "Is real-time data exchange possible?",
        content:
          "Yes. We tailor integrations to your systems' requirements, including real-time data exchange where it's needed."
      },
      {
        title: "What security measures are in place for integrations?",
        content:
          "We implement comprehensive security measures during integrations to protect your data, including encryption protocols for secure data transmission, following current industry security practices."
      }
    ],
    cta: "Talk to an Integration Expert"
  },

  "erp-integration": {
    slug: "erp-integration",
    name: "ERP Integration",
    icon: erpIntegrationIcon,
    image: erpIntegrationImg,
    imageAlt:
      "ERP platform connected to finance, supply chain and cloud systems",
    metaTitle: "ERP Integration | Paplaj Software Services",
    metaDescription:
      "Integrate ERP platforms with your finance, supply-chain, warehouse and trading systems for consistent, real-time data across the enterprise.",
    canonical: `${SITE}/services/erp-integration`,
    headline: "Seamless ERP Connectivity Across Your Enterprise",
    paragraphs: [
      "Already running an ERP? This is where we connect it to the rest of your business. We integrate your ERP platform with internal applications, third-party systems, trading platforms, warehouses, finance applications and cloud services — eliminating manual re-keying and keeping business data consistent everywhere.",
      "It's enterprise integration focused on your central system of record, so your ERP stays the single source of truth. We work to your ERP landscape and bring in specialist expertise where it adds value."
    ],
    features: [
      "ERP Platform Integration",
      "Financial System Integration",
      "Supply Chain Integration",
      "Warehouse & Logistics Integration",
      "Real-Time Data Exchange",
      "API & Middleware Solutions",
      "Data Consistency & Validation",
      "Secure ERP Connectivity"
    ],
    cta: "Discuss Your ERP Integration"
  },

  "api-development-management": {
    slug: "api-development-management",
    name: "API Development & Management",
    icon: apiDevelopmentIcon,
    image: apiDevelopmentImg,
    imageAlt:
      "API gateway connecting applications, cloud platforms and partners",
    metaTitle: "API Development & Management | Paplaj Software Services",
    metaDescription:
      "Design, secure and manage APIs that let your systems, partners and applications exchange data reliably across scalable software ecosystems.",
    canonical: `${SITE}/services/api-development-management`,
    headline: "Reliable APIs That Power Modern Business",
    paragraphs: [
      "Need to build, secure or expose APIs? This is about the connectors themselves. We design, develop, secure, document and manage the APIs that let your applications — and your partners — exchange data reliably, whether you're powering internal integrations or publishing APIs for others to use.",
      "APIs are the building blocks that integration runs on. From first design to ongoing management, we make them reliable, well-documented and easy to consume."
    ],
    features: [
      "API Design & Development",
      "API Gateway & Management",
      "API Security",
      "Authentication & Authorization",
      "API Documentation",
      "Versioning & Lifecycle Management",
      "Third-Party API Integration",
      "Monitoring & Analytics"
    ],
    cta: "Build Your API Platform"
  },

  "ai-automation": {
    slug: "ai-automation",
    name: "AI Automation",
    icon: aiAutomationIcon,
    image: aiAutomationImg,
    imageAlt: "AI automating documents, workflows and enterprise applications",
    metaTitle: "AI Automation | Paplaj Software Services",
    metaDescription:
      "An emerging capability: AI-driven automation for document processing, workflows and decision support, integrated with your existing systems.",
    canonical: `${SITE}/services/ai-automation`,
    headline: "Intelligent Automation for Smarter Operations",
    paragraphs: [
      "Artificial intelligence is opening up new ways to automate repetitive tasks, accelerate decision-making, and improve operational efficiency. This is an emerging and expanding area for us: we're actively building AI-driven automation capabilities and applying them where they deliver real, practical value.",
      "We focus on solutions that integrate cleanly with your existing systems and workflows, rather than AI for its own sake."
    ],
    features: [
      "Document Processing Automation",
      "Workflow Automation with AI",
      "LLM Integration",
      "AI Assistants & Agents",
      "Intelligent Data Extraction",
      "Decision Support",
      "AI Integration with Existing Systems",
      "Process Optimization"
    ],
    cta: "Explore AI Automation"
  },

  "workflow-automation": {
    slug: "workflow-automation",
    name: "Workflow Automation",
    icon: workflowAutomationIcon,
    image: workflowAutomationImg,
    imageAlt:
      "Business workflow connecting approvals, notifications and systems",
    metaTitle: "Workflow Automation | Paplaj Software Services",
    metaDescription:
      "Automate approvals, notifications and document flows across people, applications and business processes into scalable, reliable pipelines.",
    canonical: `${SITE}/services/workflow-automation`,
    headline: "Automate Business Processes End-to-End",
    paragraphs: [
      "Reduce manual effort and improve operational efficiency by automating business workflows. We design workflow solutions that connect people, applications, and business processes into intelligent, scalable automation pipelines.",
      "From approvals to notifications to document handling, we remove manual handoffs and reduce operational risk."
    ],
    features: [
      "Business Process Automation",
      "Approval Workflows",
      "Document Workflows",
      "Event-Driven Automation",
      "Integration Workflows",
      "Notification Systems",
      "Task Automation",
      "Monitoring Dashboards"
    ],
    cta: "Automate Your Processes"
  },

  "production-support-maintenance": {
    slug: "production-support-maintenance",
    name: "Production Support & Maintenance",
    icon: productionSupportIcon,
    image: productionSupportImg,
    imageAlt: "Operations dashboard with monitoring, alerts and support",
    metaTitle: "Production Support & Maintenance | Paplaj Software Services",
    metaDescription:
      "Monitoring, incident management and SLA-based support to keep business-critical systems stable, reduce downtime and resolve issues quickly.",
    canonical: `${SITE}/services/production-support-maintenance`,
    headline: "Reliable Support for Business-Critical Systems",
    paragraphs: [
      "Enterprise applications require continuous monitoring and proactive maintenance. Our production support services help ensure system stability, reduce downtime, and resolve issues quickly so your business operations continue without interruption.",
      "We support your systems under clear service levels, so you always know what to expect."
    ],
    features: [
      "Application Monitoring",
      "Incident Management",
      "Performance Optimization",
      "Root Cause Analysis",
      "System Health Checks",
      "Production Issue Resolution",
      "Preventive Maintenance",
      "SLA-Based Support"
    ],
    expertise: {
      title: CTRM_EXPERTISE_TITLE,
      content:
        "We provide ongoing support, health monitoring, performance optimization and upgrades for live Commodity Trading and Risk Management systems under SLA — keeping business-critical trading operations running without interruption."
    },
    faqs: [
      {
        title: "What support do you provide after a system goes live?",
        content:
          "After go-live we provide extensive support to keep operations smooth and address any issues, as outlined in the Service Level Agreement (SLA). We continuously update the system with new features, security enhancements and performance improvements."
      },
      {
        title: "How do you monitor system health?",
        content:
          "We conduct regular health checks and proactive issue resolution to identify and resolve potential problems before they impact your operations."
      },
      {
        title: "How do you optimize system performance?",
        content:
          "We continuously monitor performance metrics to identify bottlenecks, tune database queries, server configurations and application settings, and apply the latest patches and enhancements for optimal performance and security."
      },
      {
        title: "How are system upgrades managed?",
        content:
          "Upgrades follow a structured process to ensure a smooth transition with minimal disruption — assessing the current system, planning scope and timeline, implementing, and providing ongoing support afterward."
      },
      {
        title: "Is there a response time for support requests?",
        content:
          "Yes, support is provided based on the agreed Service Level Agreement (SLA)."
      }
    ],
    cta: "Keep Your Systems Running"
  },

  "cloud-devops-solutions": {
    slug: "cloud-devops-solutions",
    name: "Cloud & DevOps Solutions",
    icon: cloudDevopsIcon,
    image: cloudDevopsImg,
    imageAlt:
      "Cloud infrastructure with containers, CI/CD and deployment automation",
    metaTitle: "Cloud & DevOps Solutions | Paplaj Software Services",
    metaDescription:
      "Cloud-native architecture, CI/CD pipelines and infrastructure automation on AWS and Azure for faster, more reliable software delivery.",
    canonical: `${SITE}/services/cloud-devops-solutions`,
    headline: "Accelerate Delivery with Modern Cloud Technologies",
    paragraphs: [
      "We help organizations adopt cloud-native architectures and DevOps practices that improve scalability, reliability, and deployment speed. From infrastructure automation to CI/CD pipelines, we enable faster software delivery with enterprise-grade security.",
      "We meet you where you are — whether you're migrating to the cloud or improving an existing pipeline."
    ],
    features: [
      "Cloud Infrastructure (AWS & Azure)",
      "Cloud Migration",
      "Containerization",
      "CI/CD Pipelines",
      "Infrastructure as Code",
      "Monitoring & Logging",
      "Cloud Security",
      "DevOps Consulting"
    ],
    cta: "Modernize Your Infrastructure"
  }
};

export default services;
